import { AutoComplete, Avatar, TextHighlighter } from '@iba-team/portal-react-components/dist';
import * as React from 'react';
import { observer } from 'mobx-react';
import { FC, useEffect, useState } from 'react';
import { EmployeeDto, GlobalSearchStore } from '../../store/GlobalSearch';
import LeaveTypes from '../../components/LeaveTypes';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import { format } from 'date-fns';
import './GlobalSearch.scss';
import { PROFILE_ROUTE } from '../../utils/consts';
import { Link } from 'react-router-dom';
import { sendUsageRequest } from '../../utils/functions';
import apiConfig from '../../api/routes';
import { useDebouncedValue } from '../../utils/hooks';
import { ProjectManagementStore } from '../../store/ProjectManagement';

interface Props{
  className?: string;
  onClick?: () => void;
  widgetName?: string
}

export const GlobalSearch: FC<Props> = observer((props) => {
  const {addToRecentlySearch, searchList,
    loadedQuery,
    recentlySearch,
    recentlySearchList,
    search,
    images,
    fetchPortraitUrl,
    refreshSearchList,
  } = GlobalSearchStore;
  const { featureFlags } = ProjectManagementStore;
  const { getAllLeaveTypes, allLeaveTypes } = LeaveTypesStore;
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 400);
  const renderList = query ? searchList : recentlySearchList;
  const [selected, setSelected] = useState<EmployeeDto>({
    id: 0,
    imageLink: '',
    isAvailableForToday: false,
    lastWithFirstName: '',
    profileAccessType: '',
    requestEndDate: null,
    requestStartDate: null,
    requestTypeId: null,
    requestTypeName: null,
    isHoliday: null,
    holidayName: null
  });

  useEffect(() => {
    recentlySearch();
  }, []);

  useEffect(() => {
    fetchPortraitUrl();
  }, [recentlySearchList, searchList]);

  const onSelect = (item : EmployeeDto) => {
    setSelected(item);
    addToRecentlySearch(item.id);
  };

  useEffect(() => {
    getAllLeaveTypes();
  },[]);

  useEffect(() => {
    if (debouncedQuery.length > 0){
      search(debouncedQuery, props.widgetName);
    }
  }, [debouncedQuery]);

  const onSearch = (query: string) => {
    setQuery(query);
  };

  const getUrl = (link: string) => {
    const img = images.find(i => i.originalUrl === link);

    return img?.blob;
  };

  const onClear = () => {
    refreshSearchList();
    setQuery('');
  };

  const renderItem = (i: EmployeeDto) => {
    const handleProfileClick = () => {
      sendUsageRequest(props.widgetName ? `${apiConfig.statisticsUsageService.goToProfile}?widgetName=${props.widgetName}&profileId=${i.id}` : `${apiConfig.statisticsUsageService.goToProfile}?profileId=${i.id}`);
      props.onClick?.();
    };

    return <Link
      onClick={handleProfileClick}
      onAuxClick={handleProfileClick}
      className={'global-search-item'}
      to={`${PROFILE_ROUTE}/${i.id}`}
      target={'_blank'}
      rel="noreferrer"
    >
      <div className={'global-search-item__top'}>
        <Avatar url={getUrl(i.imageLink)} className={'global-search-item__top__avatar'}/>
        <div className={'global-search-item__top__right'}>
          <TextHighlighter data={i.lastWithFirstName} compareString={query}/>
          {(i.isAvailableForToday || (!featureFlags.SHOW_BUSINESS_TRIPS && i.requestTypeName === 'Business Trip')) ?
            <div className={'global-search-item__top__right__available'}>
              Available today
            </div>
            :
            i.isHoliday ?
              <div className={'global-search-item__top__right__holiday'}>{i.holidayName}</div>
              : 
              <div className={'global-search-item__top__right__leave-range'}>
                <LeaveTypes type={i.requestTypeName!} infoMessageVisible={false} leaveTypes={allLeaveTypes}/>
                {i.requestStartDate && <div className={'global-search-item__top__right__leave-range__dates'}>
                  {format(new Date(i.requestStartDate), 'dd.MM.yyyy')} - {i.requestEndDate ? format(new Date(i.requestEndDate), 'dd.MM.yyyy'): format(new Date(i.requestStartDate), 'dd.MM.yyyy')}
                </div>}
              </div>
          }
        </div>
      </div>
    </Link>;
  };

  const renderHeader = () => {
    if (renderList.employeesSize === 0) {
      return null;
    }

    if (query.length > 0) {
      return <div>{`Found ${searchList.employeesSize} employees`}</div>;
    }

    return <>
      <div className='global-search-header'>Type the employee's name<br/>in Latin or Cyrillic</div>
      <div>Recent searches</div>
    </>;
  };

  return(
    <AutoComplete onChange={onSearch}
      loading={(!!query && query !== loadedQuery)}
      placeholder={'Search IBA Group'}
      renderDropdownItem={renderItem}
      onClear={onClear} 
      value={query}
      customDropdownWidth={renderList.employeesSize ? '360px' : undefined}
      notFoundContent={{title: 'No results', description: <p>Check your spelling or search for a<br/>different name</p>}}
      className={`${props.className} global-search`}
      renderHeaderContent={renderHeader()}
      onSelect={(e) => onSelect(e!)} list={renderList.employees} uniqueElement={i => i.id === selected.id}/>
  );
});
